
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, Card, CardContent, Button, Chip,
  LinearProgress, IconButton, Divider
} from '@mui/material';
import {
  Notifications, CheckCircle, LocalShipping, ShoppingCart,
  RateReview, Chat, Campaign, MarkEmailRead
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { notificationAPI } from '../services/api';

const MotionCard = motion(Card);

const typeIcons = {
  order_received: <ShoppingCart fontSize="small" color="primary" />,
  order_accepted: <CheckCircle fontSize="small" color="success" />,
  order_rejected: <Campaign fontSize="small" color="error" />,
  order_shipped: <LocalShipping fontSize="small" color="info" />,
  order_delivered: <CheckCircle fontSize="small" color="success" />,
  new_message: <Chat fontSize="small" color="primary" />,
  review_received: <RateReview fontSize="small" color="warning" />,
  system: <Campaign fontSize="small" />,
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadNotifications(); }, []);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const res = await notificationAPI.getAll();
      setNotifications(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAllRead = async () => {
    await notificationAPI.markAllRead();
    setNotifications(notifications.map(n => ({ ...n, is_read: true })));
  };

  const handleClick = async (notif) => {
    if (!notif.is_read) {
      await notificationAPI.markRead(notif.id);
      setNotifications(notifications.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
    }
    if (notif.link) navigate(notif.link);
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '80vh', py: 4 }}>
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Notifications color="primary" />
            <Typography variant="h4" fontWeight={700}>Notifications</Typography>
            {unreadCount > 0 && (
              <Chip label={`${unreadCount} new`} color="error" size="small" />
            )}
          </Box>
          {unreadCount > 0 && (
            <Button startIcon={<MarkEmailRead />} onClick={handleMarkAllRead} size="small">
              Mark all read
            </Button>
          )}
        </Box>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {notifications.length === 0 && !loading && (
          <Card sx={{ p: 5, textAlign: 'center', borderRadius: 3 }}>
            <Notifications sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">No notifications yet</Typography>
          </Card>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {notifications.map((notif, i) => (
            <MotionCard
              key={notif.id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.03 }}
              sx={{
                borderRadius: 2, cursor: 'pointer',
                bgcolor: notif.is_read ? 'white' : '#eff6ff',
                border: notif.is_read ? '1px solid #e2e8f0' : '1px solid #93c5fd',
                '&:hover': { borderColor: 'primary.main' },
              }}
              onClick={() => handleClick(notif)}
            >
              <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', py: 2 }}>
                <Box sx={{ mt: 0.5 }}>
                  {typeIcons[notif.type] || <Campaign fontSize="small" />}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight={notif.is_read ? 400 : 600}>
                    {notif.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {notif.message}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.disabled" sx={{ whiteSpace: 'nowrap' }}>
                  {new Date(notif.created_at).toLocaleDateString('en-IN', {
                    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                  })}
                </Typography>
              </CardContent>
            </MotionCard>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default NotificationsPage;


